import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import StateMessage from "./StateMessage";
import "./MyLigasOptions.css";

const CACHE_KEY = "leaguesCache:v3";

const readCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
};

/**
 * Published leagues, shared by the Ligas page and the home page.
 * Serves the session cache immediately, then revalidates.
 */
export function useLeagues() {
  const [leagues, setLeagues] = useState(() => readCache()?.data || []);
  const [loading, setLoading] = useState(() => !readCache()?.data);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLeagues = async ({ showLoading } = {}) => {
      if (showLoading && !readCache()?.data) setLoading(true);

      try {
        const { data: rows, error: supabaseError } = await supabase
          .from("leagues")
          .select(
            `
            id,
            league_name,
            theme_color,
            track_players,
            track_goals,
            season,
            archived,
            finals_started
          `
          )
          .eq("is_published", true);

        if (supabaseError) throw supabaseError;

        const data = (rows || []).map((row) => ({
          ...row,
          league_id: row.id,
        }));

        if (cancelled) return;

        setLeagues(data);
        setError(null);
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data, ts: Date.now() })
        );
      } catch (err) {
        console.error("Error fetching leagues:", err);
        if (!cancelled && !readCache()?.data) {
          setError("No se pudieron cargar las ligas.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Always revalidate — publish/unpublish must show up on revisit
    fetchLeagues({ showLoading: true });

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchLeagues({ showLoading: false });
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return { leagues, loading, error };
}

export function LeagueListSkeleton({ rows = 3 }) {
  return (
    <div className="ligas-list" role="status" aria-label="Cargando ligas…">
      {Array.from({ length: rows }).map((_, i) => (
        <span key={i} className="skeleton liga-skeleton" />
      ))}
    </div>
  );
}

const MyLigasOptions = () => {
  const { leagues, loading, error } = useLeagues();

  if (loading) {
    return (
      <div className="ligas-page">
        <LeagueListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ligas-page">
        <StateMessage kind="error" title={error}>
          Revisa tu conexión e intenta de nuevo.
        </StateMessage>
      </div>
    );
  }

  const activeLeagues = leagues.filter((l) => !l.archived);
  const archivedLeagues = leagues.filter((l) => l.archived);

  return (
    <div className="ligas-page">
      <LeagueSection
        title="Ligas activas"
        subtitle="Temporadas en curso"
        leagues={activeLeagues}
        emptyLabel="No hay ligas activas"
      />
      <LeagueSection
        title="Ligas pasadas"
        subtitle="Archivo de temporadas anteriores"
        leagues={archivedLeagues}
        emptyLabel="No hay ligas pasadas"
        archived
      />
    </div>
  );
};

function LeagueSection({ title, subtitle, leagues, emptyLabel, archived }) {
  return (
    <section className={`ligas-section${archived ? " archived" : ""}`}>
      <header className="ligas-section-header">
        <h2 className="ligas-section-title">
          {title}
          {leagues.length > 0 && (
            <span className="ligas-count">{leagues.length}</span>
          )}
        </h2>
        <p className="ligas-section-subtitle">{subtitle}</p>
      </header>

      <div className="ligas-list">
        {leagues.length ? (
          leagues.map((league) => (
            <LeagueLink key={league.league_id} league={league} />
          ))
        ) : (
          <StateMessage>{emptyLabel}</StateMessage>
        )}
      </div>
    </section>
  );
}

export function LeagueLink({ league }) {
  const theme = league.theme_color || "var(--accent)";

  return (
    <Link
      to={`/ligas/${league.league_id}`}
      className="liga-item"
      style={{ "--liga-theme": theme }}
    >
      <span className="liga-accent" aria-hidden />
      <span className="liga-content">
        <span className="liga-name">{league.league_name}</span>
        <span className="liga-meta">
          {league.season && (
            <span className="liga-season">Temporada {league.season}</span>
          )}
          {!!league.finals_started && !league.archived && (
            <span className="liga-badge">Finales</span>
          )}
          {!!league.archived && <span className="liga-badge muted">Archivada</span>}
        </span>
      </span>
      <svg
        className="liga-arrow"
        viewBox="0 0 20 20"
        width="18"
        height="18"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7.5 4.5 13 10l-5.5 5.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}

export default MyLigasOptions;
